// frontend/src/pages/Conditions.js — Conditions Générales d'Utilisation (CGU)
import React from "react";
import { useNavigate } from "react-router-dom";
import LegalPage, { Section, P, B, UL, LI } from "../components/LegalPage";

const Conditions = () => {
  const navigate = useNavigate();
  return (
    <LegalPage
      title="Conditions Générales d'Utilisation"
      subtitle="Les présentes conditions régissent l'accès et l'utilisation de la plateforme 1PARC. En créant un compte ou en utilisant nos services, vous acceptez ces conditions."
      lastUpdated="14 juin 2026"
    >
      <Section n={1} title="Identification de l'éditeur">
        <P>La plateforme 1PARC est éditée par <B>1 PARC SARL</B>, société à responsabilité limitée de droit ivoirien.</P>
        <UL>
          <LI>RCCM : 2014-M1 du 08-09-2023</LI>
          <LI>Numéro de Compte Contribuable (NCC) : 2401579K — Régime d'imposition : RSI — Centre des impôts : 970 Impôts de Williamsville</LI>
          <LI>Siège social : 2 Plateaux Paillet, Abidjan, Côte d'Ivoire</LI>
          <LI>Téléphone : 0799290000</LI>
          <LI>E-mail : contact@1parc.com</LI>
        </UL>
        <P>Ci-après désignée « <B>1PARC</B> », « nous » ou « la Plateforme ».</P>
      </Section>

      <Section n={2} title="Objet et acceptation">
        <P>Les présentes Conditions Générales d'Utilisation (« CGU ») ont pour objet de définir les modalités d'accès et d'utilisation de la Plateforme. Elles constituent un contrat entre 1PARC et toute personne qui crée un compte ou utilise les services (« l'Utilisateur »).</P>
        <P>L'utilisation de la Plateforme implique l'acceptation pleine et entière des présentes CGU. Si vous n'acceptez pas ces conditions, vous ne devez pas utiliser la Plateforme.</P>
      </Section>

      <Section n={3} title="Description du service">
        <P>1PARC est une plateforme numérique qui met en relation les différents acteurs de l'écosystème automobile en Côte d'Ivoire :</P>
        <UL>
          <LI><B>Propriétaires de véhicules</B> : suivi de l'entretien, demandes de service, réception de devis, paiement et historique.</LI>
          <LI><B>Ateliers de réparation</B> : réception de demandes, émission de devis et de factures, encaissement.</LI>
          <LI><B>Fournisseurs de pièces et assureurs</B> : participation à l'écosystème selon leurs rôles respectifs.</LI>
        </UL>
        <P>1PARC fournit l'outil technologique de mise en relation et de traçabilité. <B>1PARC n'exécute pas les travaux d'entretien ou de réparation</B> : ceux-ci relèvent de la seule responsabilité des ateliers.</P>
      </Section>

      <Section n={4} title="Inscription et comptes">
        <P>L'accès à certaines fonctionnalités nécessite la création d'un compte. L'Utilisateur s'engage à fournir des informations exactes et à les tenir à jour. Il est responsable de la confidentialité de ses identifiants.</P>
        <P><B>Comptes créés par un atelier (clients « walk-in »)</B> : un atelier partenaire peut enregistrer un véhicule et créer un compte préliminaire pour son client, à partir des informations communiquées par ce dernier. Le client reçoit alors une invitation (SMS et/ou e-mail) pour activer son compte et en prendre le contrôle. En communiquant ces informations à l'atelier, le client en autorise l'enregistrement sur la Plateforme.</P>
      </Section>

      <Section n={5} title="Obligations des utilisateurs">
        <P>L'Utilisateur s'engage à utiliser la Plateforme de manière loyale et conforme à la loi. Il est notamment interdit de :</P>
        <UL>
          <LI>fournir des informations fausses, trompeuses ou usurpées ;</LI>
          <LI>enregistrer un véhicule ou des données dont on n'est pas le propriétaire ou pour lesquels on n'a pas l'autorisation ;</LI>
          <LI>porter atteinte au fonctionnement, à la sécurité ou à l'intégrité de la Plateforme ;</LI>
          <LI>utiliser la Plateforme à des fins frauduleuses ou illicites ;</LI>
          <LI>collecter ou détourner les données d'autres utilisateurs.</LI>
        </UL>
      </Section>

      <Section n={6} title="Devis, factures et paiements">
        <P>Les ateliers émettent des devis et des factures via la Plateforme. Le prix présenté au client inclut une <B>commission de service de 1PARC (10 %)</B>, intégrée au montant client.</P>
        <P>Les paiements peuvent être effectués via le service de paiement mobile <B>Wave</B>. 1PARC agit en qualité d'<B>intermédiaire technique</B> facilitant la transaction ; les conditions du prestataire de paiement s'appliquent.</P>
        <P>La qualité, la conformité et la garantie des travaux et des pièces relèvent exclusivement de l'atelier exécutant. 1PARC n'est pas partie au contrat de prestation conclu entre le propriétaire et l'atelier et ne saurait être tenue responsable de son exécution.</P>
      </Section>

      <Section n={7} title="Propriété intellectuelle">
        <P>La Plateforme, sa marque, ses logos, son code et ses contenus sont la propriété de 1PARC ou de ses concédants, et sont protégés par le droit applicable. Toute reproduction ou utilisation non autorisée est interdite.</P>
        <P>Les données saisies par l'Utilisateur (informations de véhicule, documents, notes) demeurent les siennes ; l'Utilisateur concède à 1PARC une licence d'utilisation strictement nécessaire au fonctionnement du service.</P>
      </Section>

      <Section n={8} title="Responsabilité et limitation de garantie">
        <P>1PARC met en œuvre des moyens raisonnables pour assurer la disponibilité et la fiabilité de la Plateforme, sans garantie d'absence d'interruption ou d'erreur. La Plateforme est fournie « en l'état ».</P>
        <P>Les fonctionnalités d'aide à la décision (diagnostic par intelligence artificielle, estimations de coûts, suggestions de pièces) sont fournies à titre <B>indicatif</B> et ne remplacent pas l'avis d'un professionnel. 1PARC ne garantit pas leur exactitude.</P>
        <P>Dans les limites permises par la loi, 1PARC ne saurait être tenue responsable des dommages indirects résultant de l'utilisation de la Plateforme ou des prestations réalisées par les ateliers.</P>
      </Section>

      <Section n={9} title="Suspension et résiliation">
        <P>L'Utilisateur peut cesser d'utiliser la Plateforme à tout moment. 1PARC se réserve le droit de suspendre ou de résilier un compte en cas de manquement aux présentes CGU, de fraude ou d'usage abusif.</P>
      </Section>

      <Section n={10} title="Données personnelles">
        <P>Le traitement des données personnelles est décrit dans notre <button className="lk" onClick={() => navigate("/confidentialite")} style={{ background: "none", border: "none", padding: 0, cursor: "pointer", font: "inherit" }}>Politique de Confidentialité</button>, qui fait partie intégrante des présentes CGU.</P>
      </Section>

      <Section n={11} title="Modification des CGU">
        <P>1PARC peut modifier les présentes CGU à tout moment. La version applicable est celle en vigueur au moment de l'utilisation. Les modifications substantielles seront portées à la connaissance des Utilisateurs par les moyens appropriés.</P>
      </Section>

      <Section n={12} title="Droit applicable et juridiction">
        <P>Les présentes CGU sont régies par le <B>droit ivoirien</B>. Tout litige relatif à leur interprétation ou à leur exécution relève, à défaut de résolution amiable, de la compétence des <B>tribunaux d'Abidjan</B>.</P>
      </Section>

      <Section n={13} title="Contact">
        <P>Pour toute question relative aux présentes conditions : <B>contact@1parc.com</B> — 0799290000 — 2 Plateaux Paillet, Abidjan.</P>
      </Section>
    </LegalPage>
  );
};

export default Conditions;
